'use client';

import { cn } from '@fshr/shared-react';
import Icon, { IconOccupancy, IconTime } from '../icon';
import BevSafeText from '../bev-safe-text';
import { getTaxonomyIcon, resolveTaxonomyLabel } from './taxonomy';
import type { SplitPageHeroGuestIntent, SplitPageHeroTag } from './types';

type TagsProps = {
	durationTag?: string;
	occupancyTag?: string;
	taxonomy?: string;
	guestIntentTag?: SplitPageHeroGuestIntent;
	className?: string;
};

const ICON_SIZE = 20;

/**
 * Builds the tag row from the authored BL fields.
 *
 * Order follows UI AC 3c–3f: Tag 1 Duration, Tag 2 Max Occupancy,
 * Tag 3 Taxonomy, Tag 4 Guest Intent.
 */
export function buildSplitPageHeroTags({
	durationTag,
	occupancyTag,
	taxonomy,
	guestIntentTag,
}: Omit<TagsProps, 'className'>): SplitPageHeroTag[] {
	const tags: SplitPageHeroTag[] = [];

	// Tag 1 — BL AC: value from Duration Summary, overridden by Duration Tag Text.
	if (durationTag) {
		tags.push({ icon: <IconTime width={ICON_SIZE} height={ICON_SIZE} />, label: durationTag });
	}

	// Tag 2 — BL AC: value from Max Occupancy Summary, overridden by Max Occupancy Tag Text.
	if (occupancyTag) {
		tags.push({
			icon: <IconOccupancy width={ICON_SIZE} height={ICON_SIZE} />,
			label: occupancyTag,
		});
	}

	// Tag 3 — BL AC: the Taxonomy field + icon, using the L3 subcategory.
	const taxonomyLabel = resolveTaxonomyLabel(taxonomy);
	if (taxonomyLabel) {
		const TaxonomyIcon = getTaxonomyIcon(taxonomyLabel);
		tags.push({
			icon: <TaxonomyIcon width={ICON_SIZE} height={ICON_SIZE} />,
			label: taxonomyLabel,
		});
	}

	// Tag 4 — BL AC: Guest Intent Tag.
	if (guestIntentTag?.title && guestIntentTag.icon) {
		tags.push({
			icon: <Icon icon={guestIntentTag.icon} />,
			label: guestIntentTag.title,
		});
	}

	return tags;
}

export function SplitPageHeroTags({ className, ...fields }: TagsProps) {
	const tags = buildSplitPageHeroTags(fields);

	// UI AC 3a: "A minimum of 2 and a maximum of 4 tags render in a row. If fewer
	// than 2 tags are authored, the tags row does not render."
	if (tags.length < 2) return null;

	const visibleTags = tags.slice(0, 4);

	return (
		// Row gap (wraps to multiple lines) and each pill's own icon-to-label gap
		// share one Figma itemSpacing value (6/8/10, matching the named
		// inline-xs token); the pill's own padding is asymmetric at mobile
		// (8px horizontal / 4px vertical) but uniform 10px at desktop — the
		// horizontal half matches tag-padding-x exactly, the vertical half
		// doesn't match tag-padding-y (which stays at 6 past mobile; this
		// component's real desktop value is 10), so that one stays numeric-
		// scale. Verified against the Split Page Hero Figma frames' Pills/
		// Status message instances (desktop 1440 / mobile 375).
		<div
			className={cn(
				'flex flex-wrap items-center gap-x-inline-xs gap-y-stack-xs md:gap-y-150',
				className
			)}
		>
			{visibleTags.map((tag, index) => (
				<div
					key={index}
					className="inline-flex items-center gap-inline-xs px-tag-padding-x py-tag-padding-y md:py-100 text-caption text-text-primary"
					style={{ backgroundColor: 'rgba(203, 196, 188, 0.2)', borderRadius: '5px' }}
				>
					{tag.icon}
					<span>
						<BevSafeText>{tag.label}</BevSafeText>
					</span>
				</div>
			))}
		</div>
	);
}
